import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../../context/AuthContext.js';
import LocationFilter from '../locaties/LocationFilter.js';
import './MeetingRounds.css';

function AllMeetingRounds() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('jwtToken');
  const { userInfo, isAuthenticated } = useAuth();

  const userRole = userInfo?.role || '';
  const isSingleUser = Boolean(userInfo?.is_single_user);

  const [meetingRounds, setMeetingRounds] = useState([]);
  const [locations, setLocations] = useState([]);
  const [docenten, setDocenten] = useState([]);
  const [locationFilter, setLocationFilter] = useState('all');

  const [zoekterm, setZoekterm] = useState('');
  const [debouncedZoekterm, setDebouncedZoekterm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [schoolYearFilter, setSchoolYearFilter] = useState('all');

  const [isLoading, setIsLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false
  });

  axios.defaults.withCredentials = true;

  const canManageRounds =
    userRole === 'super_admin' ||
    userRole === 'locatie_admin' ||
    userRole === 'docent';

  const canPublishOrInvite =
    userRole === 'super_admin' ||
    userRole === 'locatie_admin';

  const statusLabelMap = {
    draft: 'Concept',
    published: 'Gepubliceerd',
    closed: 'Gesloten',
    cancelled: 'Geannuleerd'
  };

  const statusBadgeClassMap = {
    draft: 'badge bg-secondary',
    published: 'badge bg-success',
    closed: 'badge bg-dark',
    cancelled: 'badge bg-danger'
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedZoekterm(zoekterm.trim());
    }, 400);

    return () => clearTimeout(timer);
  }, [zoekterm]);

  const getLocationName = (id) => {
    if (!id || isSingleUser) return '-';
    const loc = locations.find((l) => l.location_id === id);
    return loc ? loc.location_name : '-';
  };

  const getDocentName = (docent) => {
    if (!docent) return '';
    const fullName = `${docent.firstname || ''} ${docent.lastname || ''}`.trim();
    return fullName || docent.name || docent.email || docent.docent_id || '';
  };

  const getRoundDocentenText = (round) => {
    if (Array.isArray(round.docenten) && round.docenten.length > 0) {
      return round.docenten
        .map((docent) => {
          if (typeof docent === 'string') {
            const found = docenten.find((item) => item.docent_id === docent);
            return getDocentName(found) || docent;
          }

          const found = docent?.docent_id
            ? docenten.find((item) => item.docent_id === docent.docent_id)
            : null;

          return getDocentName(found) || getDocentName(docent);
        })
        .filter(Boolean)
        .join(', ');
    }

    if (Array.isArray(round.docent_names) && round.docent_names.length > 0) {
      return round.docent_names.join(', ');
    }

    if (typeof round.docent_names === 'string' && round.docent_names.trim()) {
      return round.docent_names;
    }

    if (Array.isArray(round.docent_ids) && round.docent_ids.length > 0) {
      return round.docent_ids
        .map((docentId) => {
          const found = docenten.find((docent) => docent.docent_id === docentId);
          return getDocentName(found) || docentId;
        })
        .filter(Boolean)
        .join(', ');
    }

    return '-';
  };

  const getBookingStartText = (booking) => {
    return formatDateTime(
      booking.start_at ||
      booking.booking_start_at ||
      booking.slot_start_at ||
      booking.start_time
    );
  };

  useEffect(() => {
    if (userRole === 'super_admin' || userRole === 'locatie_admin') {
      axios
        .get(`${process.env.REACT_APP_BACKEND_URL}/location/alllocations`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        .then((res) => {
          const locs = res.data.data || res.data;
          setLocations(Array.isArray(locs) ? locs : []);
        })
        .catch((err) => {
          console.error('Error fetching locations', err);
          setLocations([]);
        });
    } else {
      setLocations([]);
    }
  }, [userRole, token]);


  useEffect(() => {
    if (userRole === 'super_admin' || userRole === 'locatie_admin') {
      axios
        .get(`${process.env.REACT_APP_BACKEND_URL}/docent/allnames`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        .then((res) => {
          const data = res.data.docenten || res.data.data || res.data.Data || res.data;
          setDocenten(Array.isArray(data) ? data : []);
        })
        .catch((err) => {
          console.error('Error fetching docent names', err);
          setDocenten([]);
        });
    } else {
      setDocenten([]);
    }
  }, [userRole, token]);

  const fetchMeetingRounds = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/meeting-round/getall`,
        {
          params: {
            page,
            limit,
            search: debouncedZoekterm,
            location_id: locationFilter,
            status: statusFilter,
            school_year: schoolYearFilter
          },
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setMeetingRounds(response.data.data || response.data.Data || []);
      setPagination(
        response.data.pagination || {
          page: 1,
          limit,
          total: 0,
          totalPages: 1,
          hasNextPage: false,
          hasPrevPage: false
        }
      );
    } catch (error) {
      console.error('Error fetching meeting rounds', error);
      setMeetingRounds([]);
      setPagination({
        page: 1,
        limit,
        total: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false
      });
    } finally {
      setIsLoading(false);
    }
  }, [token, page, limit, debouncedZoekterm, locationFilter, statusFilter, schoolYearFilter]);

  useEffect(() => {
    fetchMeetingRounds();
  }, [fetchMeetingRounds]);

  const formatDateTime = (value) => {
    if (!value) return '-';

    try {
      return new Intl.DateTimeFormat('nl-NL', {
        dateStyle: 'medium',
        timeStyle: 'short'
      }).format(new Date(value));
    } catch {
      return value;
    }
  };

  const handleViewRound = (id) => {
    navigate(`/meeting-rounds/edit/${id}`);
  };


  const getShortDescription = (text, wordCount = 5) => {
    if (!text) return '';

    const words = text.trim().split(/\s+/);

    if (words.length <= wordCount) {
      return text;
    }

    return `${words.slice(0, wordCount).join(' ')}...`;
  };


  const handlePublishRound = async (roundId, roundTitle) => {
    const result = await Swal.fire({
      title: 'Weet je het zeker?',
      html: `Wil je <strong>${roundTitle}</strong> publiceren?<br><br>Dit zet de ronde op gepubliceerd en maakt de uitnodigingen klaar voor ouders.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Ja, publiceren',
      cancelButtonText: 'Annuleren',
      reverseButtons: true,
      focusCancel: true
    });

    if (!result.isConfirmed) return;

    setActionLoadingId(roundId);

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/meeting-round/publish/${roundId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      await Swal.fire({
        icon: 'success',
        title: 'Gepubliceerd',
        text: 'De oudergesprek-ronde is succesvol gepubliceerd.'
      });

      fetchMeetingRounds();
    } catch (err) {
      console.error('Error publishing meeting round', err);

      await Swal.fire({
        icon: 'error',
        title: 'Publiceren mislukt',
        text: err?.response?.data?.error || 'Publiceren mislukt.'
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDeleteRound = async (roundId, roundTitle) => {
    const result = await Swal.fire({
      title: 'Weet je het zeker?',
      html: `Wil je <strong>${roundTitle}</strong> verwijderen?<br><br>Dit verwijdert ook gekoppelde slots, invites, boekingen en planner-items.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ja, verwijderen',
      cancelButtonText: 'Annuleren',
      confirmButtonColor: '#d33',
      reverseButtons: true,
      focusCancel: true
    });

    if (!result.isConfirmed) return;

    setActionLoadingId(roundId);

    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/meeting-round/delete/${roundId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      await Swal.fire({
        icon: 'success',
        title: 'Verwijderd',
        text: 'De oudergesprek-ronde is succesvol verwijderd.'
      });

      if (meetingRounds.length === 1 && page > 1) {
        setPage((prev) => Math.max(prev - 1, 1));
      } else {
        fetchMeetingRounds();
      }
    } catch (err) {
      console.error('Error deleting meeting round', err);

      await Swal.fire({
        icon: 'error',
        title: 'Verwijderen mislukt',
        text: err?.response?.data?.error || 'Verwijderen mislukt.'
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  const renderPaginationButtons = () => {
    if ((pagination.totalPages || 1) <= 1) return null;

    const currentPage = pagination.page || 1;
    const totalPages = pagination.totalPages || 1;

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (currentPage <= 2) endPage = Math.min(totalPages, 5);
    if (currentPage >= totalPages - 1) startPage = Math.max(1, totalPages - 4);

    const buttons = [];

    for (let i = startPage; i <= endPage; i += 1) {
      buttons.push(
        <button
          key={i}
          type="button"
          className={`btn btn-sm ${i === currentPage ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => setPage(i)}
        >
          {i}
        </button>
      );
    }

    return buttons;
  };

  const startRecord =
    pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;

  const endRecord =
    pagination.total === 0
      ? 0
      : Math.min(pagination.page * pagination.limit, pagination.total);

  return (
    <div className="container-fluid px-3 px-md-5 mt-4">
      {isAuthenticated ? (
        <>
          <div className="row mb-4 align-items-center">
            <div className="col-12 col-md-6 text-center text-md-start mb-2 mb-md-0">
              <h2 className="h4">Overzicht oudergesprek-rondes</h2>
            </div>

            <div className="col-12 col-md-6 text-end text-md-center">
              {canManageRounds && (
                <Link to="/meeting-rounds/new" className="btn btn-success">
                  <i className="bi bi-plus-circle me-2"></i>Nieuwe ronde
                </Link>
              )}
            </div>
          </div>

          {!isSingleUser && (userRole === 'super_admin' || userRole === 'locatie_admin') && (
            <div className="mb-3">
              <LocationFilter
                userRole={userRole}
                locationFilter={locationFilter}
                setLocationFilter={(value) => {
                  setLocationFilter(value);
                  setPage(1);
                }}
                locations={locations}
              />
            </div>
          )}

          <div className="row mb-3">
            <div className="col-12 col-md-4 mb-2">
              <input
                type="text"
                className="form-control rounded-0"
                placeholder="Zoek op titel of omschrijving"
                value={zoekterm}
                onChange={(e) => {
                  setZoekterm(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            <div className="col-12 col-md-2 mb-2">
              <select
                className="form-select rounded-0"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="all">Alle statussen</option>
                <option value="draft">Concept</option>
                <option value="published">Gepubliceerd</option>
                <option value="closed">Gesloten</option>
                <option value="cancelled">Geannuleerd</option>
              </select>
            </div>

            <div className="col-12 col-md-2 mb-2">
              <input
                type="text"
                className="form-control rounded-0"
                placeholder="Schooljaar"
                value={schoolYearFilter === 'all' ? '' : schoolYearFilter}
                onChange={(e) => {
                  setSchoolYearFilter(e.target.value.trim() || 'all');
                  setPage(1);
                }}
              />
            </div>

            <div className="col-12 col-md-2 mb-2">
              <select
                className="form-select rounded-0"
                value={limit}
                onChange={handleLimitChange}
              >
                <option value={10}>Toon 10</option>
                <option value={20}>Toon 20</option>
                <option value={50}>Toon 50</option>
                <option value={100}>Toon 100</option>
              </select>
            </div>

            <div className="col-12 col-md-2 mb-2 d-grid">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                  setZoekterm('');
                  setStatusFilter('all');
                  setSchoolYearFilter('all');
                  setLocationFilter('all');
                  setLimit(20);
                  setPage(1);
                }}
              >
                Filters wissen
              </button>
            </div>
          </div>

          <div className="row mb-3 align-items-center">
            <div className="col-12">
              <small className="text-muted">
                Toont {startRecord} t/m {endRecord} van {pagination.total || 0} rondes
              </small>
            </div>
          </div>

          {isLoading ? (
            <p>Loading...</p>
          ) : (
            <>
              <div className="row">
                {meetingRounds.map((round) => (
                  <div key={round.round_id} className="col-12 col-md-6 col-lg-4 mb-4">
                    <div
                      className="card h-100 shadow-sm meeting-round-card"
                      onClick={() => handleViewRound(round.round_id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="card-body d-flex flex-column">
                        <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
                          <h5 className="card-title mb-0">{round.title}</h5>
                          <span className={statusBadgeClassMap[round.status] || 'badge bg-secondary'}>
                            {statusLabelMap[round.status] || round.status}
                          </span>
                        </div>

                        <p className="text-muted small mb-3">
                          {!round.description ? (
                            'Geen omschrijving opgegeven.'
                          ) : expandedDescriptions[round.round_id] ? (
                            <>
                              {round.description}{' '}
                              <button
                                type="button"
                                className="btn btn-link p-0 align-baseline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedDescriptions((prev) => ({
                                    ...prev,
                                    [round.round_id]: false
                                  }));
                                }}
                              >
                                minder
                              </button>
                            </>
                          ) : (
                            <>
                              {getShortDescription(round.description, 5)}{' '}
                              {round.description.trim().split(/\s+/).length > 5 && (
                                <button
                                  type="button"
                                  className="btn btn-link p-0 align-baseline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedDescriptions((prev) => ({
                                      ...prev,
                                      [round.round_id]: true
                                    }));
                                  }}
                                >
                                  meer
                                </button>
                              )}
                            </>
                          )}
                        </p>

                        <ul className="list-unstyled small mb-3">
                          <li><strong>Schooljaar:</strong> {round.school_year || '-'}</li>
                          {!isSingleUser && (
                            <li><strong>Locatie:</strong> {getLocationName(round.location_id)}</li>
                          )}
                          {userRole !== 'docent' ? (
                            <li><strong>Docenten:</strong> {getRoundDocentenText(round)}</li>
                          ) : null}

                          <li><strong>Boeken open:</strong> {formatDateTime(round.booking_open_at)}</li>
                          <li><strong>Boeken sluit:</strong> {formatDateTime(round.booking_close_at)}</li>
                          <li><strong>Slots:</strong> {round.total_slots || 0}</li>
                          <li><strong>Boekingen:</strong> {round.total_bookings || 0}</li>

                          {Array.isArray(round.booked_people) && round.booked_people.length > 0 && (
                            <li className="mt-2">
                              <strong>Ingeschreven:</strong>
                              <ul className="mb-0 mt-1 ps-3">
                                {round.booked_people.map((booking, index) => (
                                  <li key={`${booking.booking_id || index}`}>
                                    {booking.student_name} — {getBookingStartText(booking)}
                                  </li>
                                ))}
                                {(round.extra_bookings_count || 0) > 0 && (
                                  <li className="text-muted">
                                    en nog {round.extra_bookings_count} meer...
                                  </li>
                                )}
                              </ul>
                            </li>
                          )}
                        </ul>

                        <div
                          className="mt-auto d-flex flex-wrap justify-content-center gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* {round.status !== 'published' && (
                            <Link
                              to={`/meeting-rounds/edit/${round.round_id}`}
                              className="btn btn-outline-primary btn-sm"
                              title="Open deze ronde om basisgegevens, groepen en slots te beheren."
                            >
                              <i className="bi bi-pencil me-1"></i>Bewerken
                            </Link>
                          )} */}

                          {round.status === 'published' ? (
                            <Link
                              to={`/meeting-rounds/edit/${round.round_id}`}
                              className="btn btn-outline-secondary btn-sm"
                              title="Bekijk deze gepubliceerde ronde."
                            >
                              <i className="bi bi-eye me-1"></i>Bekijken
                            </Link>
                          ) : (
                            <Link
                              to={`/meeting-rounds/edit/${round.round_id}`}
                              className="btn btn-outline-primary btn-sm"
                              title="Open deze ronde om basisgegevens, groepen en slots te beheren."
                            >
                              <i className="bi bi-pencil me-1"></i>Bewerken
                            </Link>
                          )}

                          {canPublishOrInvite && round.status === 'draft' && (
                            <button
                              type="button"
                              className="btn btn-success btn-sm"
                              onClick={() => handlePublishRound(round.round_id, round.title)}
                              disabled={actionLoadingId === round.round_id}
                              title="Publiceer deze ronde en zet de uitnodigingen klaar voor ouders."
                            >
                              <i className="bi bi-check2-circle me-1"></i>
                              {actionLoadingId === round.round_id ? 'Bezig...' : 'Publiceren'}
                            </button>
                          )}

                          {canPublishOrInvite && (
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleDeleteRound(round.round_id, round.title)}
                              disabled={actionLoadingId === round.round_id}
                              title="Verwijder deze oudergesprek-ronde inclusief gekoppelde records."
                            >
                              <i className="bi bi-trash me-1"></i>
                              {actionLoadingId === round.round_id ? 'Bezig...' : 'Verwijderen'}
                            </button>
                          )}
                        </div>

                        {canPublishOrInvite && (
                          <small className="text-muted d-block mt-2 text-center">
                            Publiceren zet de ronde op gepubliceerd en maakt de uitnodigingen klaar voor ouders.
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {meetingRounds.length === 0 && (
                  <div className="col-12 text-center text-muted">
                    Geen oudergesprek-rondes gevonden.
                  </div>
                )}
              </div>

              {(pagination.totalPages || 1) > 1 && (
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 mt-3">
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={!pagination.hasPrevPage}
                      onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    >
                      Vorige
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={!pagination.hasNextPage}
                      onClick={() => setPage((prev) => prev + 1)}
                    >
                      Volgende
                    </button>
                  </div>

                  <div className="d-flex flex-wrap justify-content-center gap-2">
                    {renderPaginationButtons()}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <p>Je bent niet geauthenticeerd.</p>
      )}
    </div>
  );
}

export default AllMeetingRounds;